/**
 * /r/membresia-video — video Mux de un solo uso para recuperación de membresía.
 * El token en ?t= se consume al reproducir (POST consume).
 *
 * UX (session 019f4730): debe sentirse como TikTok real —
 * full viewport cover, sin chrome Mux, un CTA claro abajo.
 */

import MuxPlayer from "@mux/mux-player-react";
import { Button } from "@/components/ui/button";
import { getBrandDisplayName } from "@/lib/brandConfig";
import { detectCountryCode } from "@/lib/countryDetection";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import { resolveCheckoutCountryCode } from "@/lib/resolveCheckoutCountryCode";
import {
  localeForPlansFromMuxLanguage,
  startVipMembershipVideoCheckout,
} from "@/lib/vipMembershipVideoCheckout";
import { cn } from "@/lib/utils";
import { Loader2, Play, Volume2, VolumeX } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";

type PageState =
  | { kind: "loading" }
  | { kind: "invalid"; message: string }
  | {
      kind: "ready";
      stage: "paid" | "registered";
      ctaUrl: string;
      email?: string;
      expiresAt?: string;
    }
  | {
      kind: "playing";
      playbackId: string;
      ctaUrl: string;
      stage: "paid" | "registered";
      email?: string;
    };

const MUX_IMMERSIVE_CSS = `
  membership-recovery-mux mux-player {
    --controls: none;
    --center-play-button: none;
    --media-object-fit: cover;
    --media-object-position: center;
    --dialog: none;
    width: 100% !important;
    height: 100% !important;
    max-height: 100dvh !important;
    background: #000 !important;
  }
  membership-recovery-mux mux-player::part(video) {
    object-fit: cover !important;
    width: 100% !important;
    height: 100% !important;
  }
  membership-recovery-mux mux-player media-error-dialog,
  membership-recovery-mux mux-player .autoplay-unmute,
  membership-recovery-mux mux-player [class*="autoplay-unmute"] {
    display: none !important;
  }
`;

export default function MembershipRecoveryVideo() {
  const [params] = useSearchParams();
  const location = useLocation();
  const token = (params.get("t") || params.get("token") || "").trim();
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [consuming, setConsuming] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef<{ play?: () => Promise<void>; muted?: boolean } | null>(null);

  const countryCode = useMemo(
    () => resolveCheckoutCountryCode(detectCountryCode()),
    [],
  );

  // Lock viewport like TikTok / DualCTA (prevents iOS rubber-band under the reel)
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyWidth: body.style.width,
      bodyHeight: body.style.height,
      bodyTop: body.style.top,
    };
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.width = "100%";
    body.style.height = "100dvh";
    body.style.top = "0";
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.width = prev.bodyWidth;
      body.style.height = prev.bodyHeight;
      body.style.top = prev.bodyTop;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setState({ kind: "invalid", message: "Falta el link del video." });
      return;
    }

    let active = true;
    fetch(`/api/membership-recovery-video?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json();
        if (!active) return;
        if (!r.ok) {
          setState({
            kind: "invalid",
            message: (data as { error?: string }).error || "Link inválido o expirado",
          });
          return;
        }
        setState({
          kind: "ready",
          stage: (data.stage === "registered" ? "registered" : "paid") as
            | "paid"
            | "registered",
          ctaUrl: String(data.cta_url || "/portal/registro"),
          email:
            typeof data.email === "string" && data.email.includes("@")
              ? data.email.trim().toLowerCase()
              : undefined,
          expiresAt: data.expires_at ? String(data.expires_at) : undefined,
        });
      })
      .catch(() => {
        if (active) setState({ kind: "invalid", message: "No se pudo validar el link." });
      });

    return () => {
      active = false;
    };
  }, [token]);

  const startVideo = useCallback(async () => {
    if (!token || state.kind !== "ready") return;
    setConsuming(true);
    try {
      const r = await fetch("/api/membership-recovery-video", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await r.json();
      if (!r.ok) {
        setState({
          kind: "invalid",
          message: (data as { error?: string }).error || "No se pudo abrir el video",
        });
        return;
      }
      setState({
        kind: "playing",
        playbackId: String(data.playback_id),
        ctaUrl: String(data.cta_url || state.ctaUrl),
        stage: state.stage,
        email:
          (typeof data.email === "string" && data.email.includes("@")
            ? data.email.trim().toLowerCase()
            : undefined) || state.email,
      });
      setIsMuted(true);
      setIsPlaying(false);
    } catch {
      setState({ kind: "invalid", message: "Error de conexión al abrir el video." });
    } finally {
      setConsuming(false);
    }
  }, [token, state]);

  /** Primary CTA → Stripe VIP subscription (same path as DualCTA / invitacion-premium). */
  const startStripeVipCheckout = useCallback(async () => {
    if (checkoutBusy) return;
    setCheckoutBusy(true);
    const email =
      state.kind === "playing" || state.kind === "ready" ? state.email : undefined;
    try {
      await startVipMembershipVideoCheckout({
        countryCode: countryCode === "UNKNOWN" ? "US" : countryCode,
        pathname: location.pathname,
        languageForPlans: localeForPlansFromMuxLanguage("es"),
        email,
        template: "MembershipRecoveryVideo",
        template_type: "MembershipRecoveryVideo",
      });
    } catch {
      setCheckoutBusy(false);
    }
  }, [checkoutBusy, countryCode, location.pathname, state]);

  const toggleMute = useCallback(() => {
    const next = !isMuted;
    setIsMuted(next);
    try {
      const p = playerRef.current as
        | (HTMLElement & { muted?: boolean; media?: HTMLVideoElement })
        | null;
      if (p) {
        p.muted = next;
        const media =
          (p as { media?: HTMLVideoElement }).media ||
          p.querySelector?.("video") ||
          null;
        if (media) {
          media.muted = next;
          if (!next) media.volume = 1;
        }
      }
    } catch {
      /* ignore */
    }
  }, [isMuted]);

  const brandLine = getBrandDisplayName();
  const vipCtaLabel = "Unirme ahora";

  return (
    <div
      className="fixed inset-0 z-0 flex h-dvh min-h-dvh w-full flex-col overflow-hidden bg-black text-white"
      style={{
        WebkitTransform: "translate3d(0,0,0)",
        transform: "translate3d(0,0,0)",
        touchAction: "none",
        overscrollBehavior: "none",
        maxHeight: "100dvh",
      }}
      data-ph-section="membership_recovery_video"
      data-ph-section-label="Membership Recovery Video"
    >
      <style>{MUX_IMMERSIVE_CSS}</style>

      {/* Loading / invalid / ready — still full black reel frame */}
      {state.kind !== "playing" && (
        <div className="relative flex h-full w-full flex-col">
          {/* Soft brand backdrop */}
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse 80% 55% at 50% 35%, rgba(172,57,242,0.22) 0%, transparent 62%), linear-gradient(180deg, #0a0a0b 0%, #000 100%)",
            }}
            aria-hidden
          />

          <header
            className="relative z-10 flex shrink-0 items-center justify-center px-4"
            style={{
              paddingTop: "max(1rem, env(safe-area-inset-top))",
              paddingBottom: "0.75rem",
            }}
          >
            <div className="text-center">
              <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-white/45">
                Mensaje personal
              </p>
              <p className="mt-1 text-sm font-medium tracking-wide text-white/90">{brandLine}</p>
            </div>
          </header>

          <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6">
            {state.kind === "loading" && (
              <div className="flex flex-col items-center gap-3 text-white/60">
                <Loader2 className="h-8 w-8 animate-spin text-amber-300/90" />
                <p className="text-sm">Validando tu link…</p>
              </div>
            )}

            {state.kind === "invalid" && (
              <div className="w-full max-w-sm space-y-5 text-center">
                <p className="text-sm leading-relaxed text-white/75">{state.message}</p>
                <Button
                  asChild
                  variant="outline"
                  className="h-12 w-full rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10"
                >
                  <Link to="/portal-acceso">Ver guía de acceso</Link>
                </Button>
              </div>
            )}

            {state.kind === "ready" && (
              <div className="flex w-full max-w-sm flex-col items-center gap-8">
                <button
                  type="button"
                  onClick={() => void startVideo()}
                  disabled={consuming}
                  className={cn(
                    "group relative flex h-[5.5rem] w-[5.5rem] items-center justify-center rounded-full",
                    "bg-white text-black shadow-[0_12px_40px_rgba(0,0,0,0.45)]",
                    "ring-4 ring-white/15 transition active:scale-95",
                    "disabled:opacity-60",
                  )}
                  aria-label="Ver video"
                >
                  {consuming ? (
                    <Loader2 className="h-9 w-9 animate-spin" />
                  ) : (
                    <Play className="ml-1 h-10 w-10 fill-current" strokeWidth={0} />
                  )}
                </button>
                <div className="space-y-2 text-center">
                  <p className="text-[15px] font-medium leading-snug text-white/90">
                    Te dejé un video corto para terminar el acceso
                  </p>
                  <p className="text-xs leading-relaxed text-white/45">
                    Link personal · un solo uso
                  </p>
                </div>
                <Button
                  size="lg"
                  className="h-12 w-full gap-2 rounded-full bg-amber-400 font-semibold text-black hover:bg-amber-300"
                  disabled={consuming}
                  onClick={() => void startVideo()}
                >
                  {consuming ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4 fill-current" />
                  )}
                  Ver video
                </Button>
              </div>
            )}
          </main>
        </div>
      )}

      {/* Playing — true TikTok fullscreen reel + single CTA */}
      {state.kind === "playing" && (
        <div className="membership-recovery-mux relative h-full w-full">
          <div className="absolute inset-0 z-0">
            <MuxPlayer
              ref={playerRef as never}
              playbackId={state.playbackId}
              streamType="on-demand"
              autoPlay
              muted={isMuted}
              playsInline
              nohotkeys
              accentColor="#fbbf24"
              style={
                {
                  width: "100%",
                  height: "100%",
                  ["--controls" as string]: "none",
                  ["--media-object-fit" as string]: "cover",
                } as CSSProperties
              }
              onPlay={() => setIsPlaying(true)}
              onPlaying={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
          </div>

          {/* Top brand chip */}
          <div
            className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center"
            style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
          >
            <div className="rounded-full bg-black/35 px-3 py-1 backdrop-blur-md ring-1 ring-white/10">
              <p className="text-[11px] font-medium tracking-wide text-white/85">{brandLine}</p>
            </div>
          </div>

          {/* Tap center to toggle mute (TikTok-like) */}
          <button
            type="button"
            className="absolute inset-0 z-10 cursor-pointer bg-transparent"
            aria-label={isMuted ? "Activar sonido" : "Silenciar"}
            onClick={toggleMute}
          />

          {/* Mute pill — only while muted / first seconds */}
          {isMuted && isPlaying && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleMute();
              }}
              className="absolute left-1/2 top-[42%] z-30 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-2"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/30 bg-black/55 text-white shadow-lg backdrop-blur-md animate-pulse">
                <VolumeX className="h-7 w-7" strokeWidth={2.25} />
              </span>
              <span className="rounded-full border border-white/20 bg-black/55 px-3 py-1 text-[12px] font-semibold text-white backdrop-blur-md">
                Toca para escuchar
              </span>
            </button>
          )}

          {!isMuted && isPlaying && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleMute();
              }}
              className="absolute z-30 rounded-full bg-black/40 p-2.5 text-white/80 backdrop-blur-sm ring-1 ring-white/15"
              style={{
                top: "max(0.75rem, env(safe-area-inset-top))",
                right: "max(0.75rem, env(safe-area-inset-right))",
              }}
              aria-label="Silenciar"
            >
              <Volume2 className="h-4 w-4" />
            </button>
          )}

          {/* Bottom scrim + single CTA */}
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-30"
            style={{
              background:
                "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.45) 55%, transparent 100%)",
              paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))",
            }}
          >
            <div className="pointer-events-auto mx-auto w-full max-w-md space-y-3 px-5 pb-2 pt-16">
              <p className="text-center text-[12px] leading-snug text-white/70">
                Membresía exclusiva · pago seguro con Stripe
              </p>
              <button
                type="button"
                disabled={checkoutBusy}
                onClick={(e) => {
                  e.stopPropagation();
                  void startStripeVipCheckout();
                }}
                className={cn(
                  "flex h-14 w-full items-center justify-center gap-2 rounded-full",
                  "bg-amber-400 text-[15px] font-bold tracking-tight text-black",
                  "shadow-[0_8px_28px_rgba(251,191,36,0.35)]",
                  "transition active:scale-[0.98] active:bg-amber-300",
                  "disabled:opacity-70",
                )}
              >
                {checkoutBusy ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : null}
                {checkoutBusy ? "Redirigiendo a Stripe…" : vipCtaLabel}
              </button>
              {/* Ya pagué → embudo post-pago: registro/perfil si hace falta, luego pending.
                  Claimed paid lands on /pending-approval so Clerk metadata stays pending. */}
              <a
                href={`${MEMBERSHIP_ROUTE.PENDING_APPROVAL}?from_recovery=1&claimed_paid=1${
                  state.email ? `&email=${encodeURIComponent(state.email)}` : ""
                }`}
                className="block text-center text-[11px] text-white/45 underline-offset-2 hover:text-white/70 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                Ya pagué — ir a revisión
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
